const transformSnakeToCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
};

export type CamelizedType<T> = T extends (...args: unknown[]) => unknown
  ? T
  : T extends readonly (infer U)[]
    ? CamelizedType<U>[]
    : T extends object
      ? {
          [K in keyof T as K extends string ? CamelizedString<K> : K]: CamelizedType<T[K]>;
        }
      : T;

interface AccessorColumn {
  accessor?: string;
  accessorKey?: string;
}

type CamelizedString<T extends string> = T extends `${infer Head}_${infer Tail}`
  ? `${Head}${Capitalize<CamelizedString<Tail>>}`
  : T;

type TransformedAccessorColumn<T extends AccessorColumn> = AccessorColumn &
  Omit<T, 'accessor' | 'accessorKey'>;

const transformCamelToSnakeCase = (str: string): string => {
  return str.replace(/([A-Z])/g, '_$1').toLowerCase();
};

const isObject = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
};

const transformObjectCase = (obj: unknown, transformKey: (key: string) => string): unknown => {
  if (Array.isArray(obj)) {
    return obj.map((item) => transformObjectCase(item, transformKey));
  }

  if (isObject(obj)) {
    return Object.keys(obj).reduce(
      (acc, key) => {
        acc[transformKey(key)] = transformObjectCase(obj[key], transformKey);
        return acc;
      },
      {} as Record<string, unknown>
    );
  }

  return obj;
};

export const transformObjectToCamelCase = (obj: unknown): unknown => {
  return transformObjectCase(obj, transformSnakeToCamelCase);
};

export const transformObjectToSnakeCase = (obj: unknown): unknown => {
  return transformObjectCase(obj, transformCamelToSnakeCase);
};

/**
 * convert accessors for table data to align them with the transformed table data.
 * {accessor: 'completion_rate_pct'} => {accessor: 'completionRatePct'}
 */
export const getColumnsWithTransformedValues = <T extends AccessorColumn>(
  columns: T[]
): TransformedAccessorColumn<T>[] => {
  return columns.map((column) => {
    const accessor = column?.accessor
      ? transformSnakeToCamelCase(column.accessor ?? '')
      : column?.accessor;
    const accessorKey = column?.accessorKey
      ? transformSnakeToCamelCase(column.accessorKey ?? '')
      : column?.accessorKey;

    return {
      ...column,
      accessor,
      accessorKey,
    };
  });
};
